import React from 'react';
import { Input, Button, message } from 'antd';

interface Page3TabProps {
  formData: Record<string, unknown>;
  isEditing: boolean;
  isApproved: boolean;
  getField: (key: string) => unknown;
  setField: (key: string, value: unknown) => void;
}

/** A single dyeing layer row */
const DyeingLayerRow: React.FC<{
  label: string;
  prefix: string; // e.g. 'fj740_30' for layer 1
  disabled: boolean;
  getField: (key: string) => unknown;
  setField: (key: string, value: unknown) => void;
}> = ({ label, prefix, disabled, getField, setField }) => {
  const str = (suffix: string) => (getField(`${prefix}${suffix}`) as string) || '';
  const num = (suffix: string) => (getField(`${prefix}${suffix}`) as number) || 0;
  // suffixes: 1=工繳代號, 2=大貨價, 3=試樣價, 4=單位, 5=縮率, 6=備註, 7=加工廠

  return (
    <tr>
      <td className="j219-dye-label">{label}</td>
      <td>
        <Input data-contract-field={`${prefix}1`} value={str('1')} disabled={disabled} size="small" style={{ width: 80 }}
          onChange={e => setField(`${prefix}1`, e.target.value)} />
      </td>
      <td>
        <Input data-contract-field={`${prefix}2`} type="number" value={num('2')} disabled={disabled} size="small" style={{ width: 80 }}
          onChange={e => setField(`${prefix}2`, parseFloat(e.target.value) || 0)} />
      </td>
      <td>
        <Input data-contract-field={`${prefix}3`} type="number" value={num('3')} disabled={disabled} size="small" style={{ width: 80 }}
          onChange={e => setField(`${prefix}3`, parseFloat(e.target.value) || 0)} />
      </td>
      <td>
        <Input data-contract-field={`${prefix}4`} value={str('4')} disabled={disabled} size="small" style={{ width: 60 }}
          onChange={e => setField(`${prefix}4`, e.target.value)} />
      </td>
      <td>
        <Input data-contract-field={`${prefix}5`} type="number" value={num('5')} disabled={disabled} size="small" style={{ width: 70 }}
          onChange={e => setField(`${prefix}5`, parseFloat(e.target.value) || 0)} />
      </td>
      <td>
        <Input data-contract-field={`${prefix}6`} value={str('6')} disabled={disabled} size="small" style={{ width: 120 }}
          onChange={e => setField(`${prefix}6`, e.target.value)} />
      </td>
      <td>
        <Input data-contract-field={`${prefix}7`} value={str('7')} disabled={disabled} size="small" style={{ width: 80 }}
          onChange={e => setField(`${prefix}7`, e.target.value)} />
      </td>
    </tr>
  );
};

const Page3Tab: React.FC<Page3TabProps> = React.memo(({
  formData, isEditing, isApproved, getField, setField,
}) => {
  const disabled = !isEditing || isApproved;
  const str = (k: string): string => (getField(k) as string) || '';
  const num = (k: string): number => (getField(k) as number) || 0;

  const handleReportPreview = (field: string) => {
    const code = str(field);
    if (!code.trim()) {
      message.warning('請先填入報告單號');
      return;
    }
    message.info(`物化性報告預覽：${code}（功能尚未實作）`);
  };

  return (
    <div className="j219-page-content">
      {/* Region: Shape2 — 染整 */}
      <div className="j219-section-frame">
        <h4 className="j219-section-title">染整</h4>
        <div className="j219-table-wrapper">
          <table className="j219-yarn-table">
            <thead>
              <tr>
                <th></th>
                <th>工繳代號</th>
                <th>大貨價(NTD)</th>
                <th>試樣價(NTD)</th>
                <th>單位</th>
                <th>縮率(%)</th>
                <th>備註說明</th>
                <th>加工廠</th>
              </tr>
            </thead>
            <tbody>
              <DyeingLayerRow label="第1層" prefix="fj740_30" disabled={disabled} getField={getField} setField={setField} />
              <DyeingLayerRow label="第2層" prefix="fj740_31" disabled={disabled} getField={getField} setField={setField} />
              <DyeingLayerRow label="第3層" prefix="fj740_32" disabled={disabled} getField={getField} setField={setField} />
            </tbody>
          </table>
        </div>
      </div>

      {/* Region: Shape3 — 成品/物化性 */}
      <div className="j219-section-frame">
        <h4 className="j219-section-title">成品</h4>
      <div className="j219-form-row">
        <div className="j219-field">
          <label>組織</label>
          <Input value={str('fj740_51')} disabled={disabled} onChange={e => setField('fj740_51', e.target.value)} data-contract-field="fj740_51" />
        </div>
        <div className="j219-field j219-field-sm">
          <label>成份%</label>
          <Input type="number" value={num('fj740_531')} disabled={disabled} onChange={e => setField('fj740_531', parseInt(e.target.value) || 0)} data-contract-field="fj740_531" />
        </div>
        <div className="j219-field j219-field-sm">
          <label>%</label>
          <Input type="number" value={num('fj740_532')} disabled={disabled} onChange={e => setField('fj740_532', parseInt(e.target.value) || 0)} data-contract-field="fj740_532" />
        </div>
        <div className="j219-field j219-field-sm">
          <label>%</label>
          <Input type="number" value={num('fj740_533')} disabled={disabled} onChange={e => setField('fj740_533', parseInt(e.target.value) || 0)} data-contract-field="fj740_533" />
        </div>
      </div>
      <div className="j219-form-row">
        <div className="j219-field">
          <label>幅寬(內)</label>
          <Input type="number" value={num('fj740_521')} disabled={disabled} onChange={e => setField('fj740_521', parseFloat(e.target.value) || 0)} data-contract-field="fj740_521" />
        </div>
        <div className="j219-field">
          <label>幅寬(外)</label>
          <Input type="number" value={num('fj740_522')} disabled={disabled} onChange={e => setField('fj740_522', parseFloat(e.target.value) || 0)} data-contract-field="fj740_522" />
        </div>
      </div>
      <div className="j219-form-row">
        <div className="j219-field">
          <label>重量</label>
          <Input type="number" value={num('fj740_541')} disabled={disabled} onChange={e => setField('fj740_541', parseFloat(e.target.value) || 0)} data-contract-field="fj740_541" />
        </div>
        <div className="j219-field">
          <label>g/㎡</label>
          <Input type="number" value={num('fj740_542')} disabled={disabled} onChange={e => setField('fj740_542', parseFloat(e.target.value) || 0)} data-contract-field="fj740_542" />
        </div>
      </div>
      <div className="j219-form-row">
        <div className="j219-field">
          <label>工繳代號</label>
          <Input value={str('fj740_55')} disabled={disabled} onChange={e => setField('fj740_55', e.target.value)} data-contract-field="fj740_55" />
        </div>
        <div className="j219-field">
          <label>成本(NTD)</label>
          <Input type="number" value={num('fj740_561')} disabled={disabled} onChange={e => setField('fj740_561', parseFloat(e.target.value) || 0)} data-contract-field="fj740_561" />
        </div>
        <div className="j219-field j219-field-sm">
          <label>單位</label>
          <Input value={str('fj740_562')} disabled={disabled} onChange={e => setField('fj740_562', e.target.value)} data-contract-field="fj740_562" />
        </div>
      </div>
      <div className="j219-form-row">
        <div className="j219-field">
          <label>供應商</label>
          <Input value={str('fj740_57')} disabled={disabled} onChange={e => setField('fj740_57', e.target.value)} data-contract-field="fj740_57" />
        </div>
        <div className="j219-field">
          <label>工廠布號</label>
          <Input value={str('fj740_571')} disabled={disabled} onChange={e => setField('fj740_571', e.target.value)} data-contract-field="fj740_571" />
        </div>
      </div>
      <div className="j219-form-row">
        <div className="j219-field">
          <label>毛圈長(mm)</label>
          <Input type="number" value={num('fj740_581')} disabled={disabled} onChange={e => setField('fj740_581', parseFloat(e.target.value) || 0)} data-contract-field="fj740_581" />
        </div>
        <div className="j219-field">
          <label>針數(G)</label>
          <Input type="number" value={num('fj740_582')} disabled={disabled} onChange={e => setField('fj740_582', parseInt(e.target.value) || 0)} data-contract-field="fj740_582" />
        </div>
        <div className="j219-field">
          <label>口數(F)</label>
          <Input type="number" value={num('fj740_583')} disabled={disabled} onChange={e => setField('fj740_583', parseInt(e.target.value) || 0)} data-contract-field="fj740_583" />
        </div>
        <div className="j219-field">
          <label>圓徑(inch)</label>
          <Input type="number" value={num('fj740_584')} disabled={disabled} onChange={e => setField('fj740_584', parseInt(e.target.value) || 0)} data-contract-field="fj740_584" />
        </div>
      </div>

      </div>{/* close section-frame for 成品 */}

      {/* Physical Property Reports */}
      <h4 className="j219-section-title">物化性檢測報告</h4>
      <div className="j219-form-row">
        <div className="j219-field">
          <label>報告單號 1</label>
          <div className="j219-report-field">
            <Input value={str('fj740_61')} disabled={disabled} onChange={e => setField('fj740_61', e.target.value)} data-contract-field="fj740_61" />
            <Button size="small" disabled={!str('fj740_61')} onClick={() => handleReportPreview('fj740_61')}>查詢</Button>
          </div>
        </div>
        <div className="j219-field">
          <label>報告單號 2</label>
          <div className="j219-report-field">
            <Input value={str('fj740_611')} disabled={disabled} onChange={e => setField('fj740_611', e.target.value)} data-contract-field="fj740_611" />
            <Button size="small" disabled={!str('fj740_611')} onClick={() => handleReportPreview('fj740_611')}>查詢</Button>
          </div>
        </div>
        <div className="j219-field">
          <label>報告單號 3</label>
          <div className="j219-report-field">
            <Input value={str('fj740_612')} disabled={disabled} onChange={e => setField('fj740_612', e.target.value)} data-contract-field="fj740_612" />
            <Button size="small" disabled={!str('fj740_612')} onClick={() => handleReportPreview('fj740_612')}>查詢</Button>
          </div>
        </div>
      </div>
    </div>
  );
});

Page3Tab.displayName = 'Page3Tab';
export default Page3Tab;
